import { ShopifyTileComponent } from 'components/shared/shopify-tile/ShopifyTileComponent';
import {
    CodTileArt,
    HomeDeliveryTileArt,
    IntegrationDesignTileArt,
    LabelGeneratingTileArt,
    ProviderSettingsTileArt,
    TranslationsTileArt,
} from 'components/shared/shopify-tile/variants';
import { SettingUrls } from 'constants/SettingsUrls';
import { PackageProviderName, SettingKey } from 'data/ppoints/constants';
import {
    AfterCheckoutMapSettingsTabId,
    DesignSettingsTabId,
    ShippingZoneRateTabId,
} from 'data/ppoints/enums/SettingsTabId';
import useSettings from 'hooks/useSettings';
import { withTabParam } from 'hooks/useTabDeepLink';
import { IntegrationModeSetting } from 'models/ppoints/settings/SettingsToEcom';
import { ReactNode, useCallback } from 'react';
import { useNavigate } from 'react-router';
import PackageProviderService from 'services/settings/ppoints/PackageProviderService';
import { useTileHide } from './hooks/useTileHide';

export interface ShopifyTilesProps {
    provider?: PackageProviderName;
    providerCountry?: string;
}

/**
 * Column count used from the `lg` breakpoint up. 4 tiles look better as a full 2x2
 * block than as a row of 3 plus a lonely one, everything else fills up to 3 columns.
 */
const LG_COLUMNS: Record<number, string> = {
    1: 'lg:grid-cols-1',
    2: 'lg:grid-cols-2',
    3: 'lg:grid-cols-3',
    4: 'lg:grid-cols-2',
};

const SM_LAST_SPAN: Record<number, string> = {
    1: 'sm:col-span-1',
    2: 'sm:col-span-2',
};

const LG_LAST_SPAN: Record<number, string> = {
    1: 'lg:col-span-1',
    2: 'lg:col-span-2',
    3: 'lg:col-span-3',
};

const getLgColumnCount = (tileCount: number) => (tileCount === 4 ? 2 : Math.min(tileCount, 3));

/** Widens the last tile so it fills the gap left in the final row (e.g. 5 tiles over 3 columns). */
const getLastTileSpan = (tileCount: number, columns: number) => {
    if (columns < 2) return 1;

    const remainder = tileCount % columns;

    return remainder === 0 ? 1 : columns - remainder + 1;
};

export const ShopifyTiles: React.FC<ShopifyTilesProps> = ({ provider, providerCountry }) => {
    const navigate = useNavigate();
    const providerName = provider ? PackageProviderService.getDisplayName(provider) : '';
    const { settings: integrationMode } = useSettings<IntegrationModeSetting>(SettingKey.integration_mode);
    const hide = useTileHide(provider, integrationMode);

    const goToProviderSettings = useCallback(() => {
        if (!provider || !providerCountry) return;

        navigate(`${SettingUrls.shippingProviders}/${provider}/${providerCountry}`);
    }, [navigate, provider, providerCountry]);

    const goToShippingZonesAndRatesTab = useCallback(
        (tab?: ShippingZoneRateTabId) => {
            const path = `${SettingUrls.shippingZones}`;
            navigate(tab ? withTabParam(path, tab) : path);
        },
        [navigate]
    );

    const goToDesignSettingsTab = useCallback(
        (tab: DesignSettingsTabId) => {
            navigate(withTabParam(SettingUrls.designSettings, tab));
        },
        [navigate]
    );

    const goToAfterCheckoutMapTab = useCallback(
        (tab: AfterCheckoutMapSettingsTabId) => {
            if (integrationMode === IntegrationModeSetting.map) {
                goToDesignSettingsTab(DesignSettingsTabId.Translation);
            } else {
                navigate(withTabParam(SettingUrls.thankYouPageMapSettings, tab));
            }
        },
        [navigate]
    );

    const goToPaymentSettings = useCallback(() => {
        navigate(SettingUrls.paymentMapping);
    }, [navigate]);

    const tiles: { key: string; tile: ReactNode }[] = [
        !hide.tile1 &&
            provider && {
                key: 'tile1',
                tile: (
                    <ShopifyTileComponent
                        titleKey="SHOPIFY_TILE_BANNERS.TILE_1.TITLE"
                        descriptionKey={
                            !hide.tile6
                                ? 'SHOPIFY_TILE_BANNERS.TILE_1.DESCRIPTION'
                                : 'SHOPIFY_TILE_BANNERS.TILE_1.HALF_INTEGRATION_DESCRIPTION'
                        }
                        buttonKey="SHOPIFY_TILE_BANNERS.TILE_1.BUTTON_LABEL"
                        values={{ value: providerName }}
                        buttonOnClick={goToProviderSettings}
                        content={<ProviderSettingsTileArt provider={provider} />}
                    />
                ),
            },
        !hide.tile2 && {
            key: 'tile2',
            tile: (
                <ShopifyTileComponent
                    titleKey="SHOPIFY_TILE_BANNERS.TILE_2.TITLE"
                    descriptionKey="SHOPIFY_TILE_BANNERS.TILE_2.DESCRIPTION"
                    buttonKey="SHOPIFY_TILE_BANNERS.TILE_2.BUTTON_LABEL"
                    buttonOnClick={() => goToPaymentSettings()}
                    content={<CodTileArt />}
                />
            ),
        },
        !hide.tile3 && {
            key: 'tile3',
            tile: (
                <ShopifyTileComponent
                    titleKey="SHOPIFY_TILE_BANNERS.TILE_3.TITLE"
                    descriptionKey="SHOPIFY_TILE_BANNERS.TILE_3.DESCRIPTION"
                    buttonKey="SHOPIFY_TILE_BANNERS.TILE_3.BUTTON_LABEL"
                    buttonOnClick={() => goToDesignSettingsTab(DesignSettingsTabId.CartButtonDesign)}
                    content={<IntegrationDesignTileArt />}
                />
            ),
        },
        !hide.tile4 && {
            key: 'tile4',
            tile: (
                <ShopifyTileComponent
                    titleKey="SHOPIFY_TILE_BANNERS.TILE_4.TITLE"
                    descriptionKey="SHOPIFY_TILE_BANNERS.TILE_4.DESCRIPTION"
                    buttonKey="SHOPIFY_TILE_BANNERS.TILE_4.BUTTON_LABEL"
                    buttonOnClick={() => goToAfterCheckoutMapTab(AfterCheckoutMapSettingsTabId.Translation)}
                    content={<TranslationsTileArt />}
                />
            ),
        },
        !hide.tile5 && {
            key: 'tile5',
            tile: (
                <ShopifyTileComponent
                    titleKey="SHOPIFY_TILE_BANNERS.TILE_5.TITLE"
                    descriptionKey="SHOPIFY_TILE_BANNERS.TILE_5.DESCRIPTION"
                    buttonKey="SHOPIFY_TILE_BANNERS.TILE_5.BUTTON_LABEL"
                    buttonOnClick={() => goToShippingZonesAndRatesTab(ShippingZoneRateTabId.HomeDelivery)}
                    content={<HomeDeliveryTileArt />}
                />
            ),
        },
        !hide.tile6 &&
            provider && {
                key: 'tile6',
                tile: (
                    <ShopifyTileComponent
                        titleKey="SHOPIFY_TILE_BANNERS.TILE_6.TITLE"
                        descriptionKey="SHOPIFY_TILE_BANNERS.TILE_6.DESCRIPTION"
                        buttonKey="SHOPIFY_TILE_BANNERS.TILE_6.BUTTON_LABEL"
                        values={{ value: providerName }}
                        buttonOnClick={goToProviderSettings}
                        content={<LabelGeneratingTileArt provider={provider} />}
                    />
                ),
            },
    ].filter(Boolean) as { key: string; tile: ReactNode }[];

    if (!tiles.length) return null;

    const lgColumns = getLgColumnCount(tiles.length);
    const lastTileSpan = `${SM_LAST_SPAN[getLastTileSpan(tiles.length, 2)]} ${LG_LAST_SPAN[getLastTileSpan(tiles.length, lgColumns)]}`;

    return (
        <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${LG_COLUMNS[lgColumns]}`}>
            {tiles.map(({ key, tile }, index) => (
                <div key={key} className={index === tiles.length - 1 ? lastTileSpan : undefined}>
                    {tile}
                </div>
            ))}
        </div>
    );
};
